import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { notification, Icon } from 'antd';
import { useSelector } from 'react-redux';
import { request } from '~/_shared/instance';
import { supabase } from '~/lib/supabase';

// v2 suffix invalidates old tokens that carried an integer tenant_id.
// Bump this whenever the JWT payload structure changes.
var TOKEN_CACHE_KEY = function(userId) { return 'supa_token_v2_' + userId; };
var TOKEN_EXP_KEY   = function(userId) { return 'supa_token_v2_exp_' + userId; };

var getCachedToken = function(userId) {
  try {
    var token = localStorage.getItem(TOKEN_CACHE_KEY(userId));
    var exp = localStorage.getItem(TOKEN_EXP_KEY(userId));
    if (!token || !exp) return null;
    if (Date.now() >= parseInt(exp, 10)) return null;
    return token;
  } catch (e) {
    return null;
  }
};

var setCachedToken = function(userId, token) {
  try {
    localStorage.setItem(TOKEN_CACHE_KEY(userId), token);
    // Token lives 24 h on server; cache for 23 h
    localStorage.setItem(TOKEN_EXP_KEY(userId), (Date.now() + 23 * 60 * 60 * 1000).toString());
  } catch (e) {}
};

// ─── Branded in-app toast ────────────────────────────────────────────────────

var NOTIF_TYPE_CONFIG = {
  success: { color: '#1DA57A', iconType: 'check-circle' },
  error:   { color: '#ff4d4f', iconType: 'close-circle' },
  warning: { color: '#fa8c16', iconType: 'exclamation-circle' },
  info:    { color: '#a78bfa', iconType: 'info-circle' },
};

var playNotificationSound = function() {
  try {
    var AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    var ctx = new AudioCtx();
    var now = ctx.currentTime;

    // First tone — A5 (880 Hz)
    var o1 = ctx.createOscillator();
    var g1 = ctx.createGain();
    o1.connect(g1);
    g1.connect(ctx.destination);
    o1.type = 'sine';
    o1.frequency.value = 880;
    g1.gain.setValueAtTime(0.15, now);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    o1.start(now);
    o1.stop(now + 0.3);

    // Second tone — E6 (1318 Hz), delayed 120 ms
    var o2 = ctx.createOscillator();
    var g2 = ctx.createGain();
    o2.connect(g2);
    g2.connect(ctx.destination);
    o2.type = 'sine';
    o2.frequency.value = 1318.51;
    g2.gain.setValueAtTime(0, now + 0.12);
    g2.gain.setValueAtTime(0.13, now + 0.14);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    o2.start(now + 0.12);
    o2.stop(now + 0.5);
  } catch (e) {}
};

var timeAgo = function(date) {
  var now = Date.now();
  var then = date instanceof Date ? date.getTime() : now;
  var diff = Math.floor((now - then) / 1000);
  if (diff < 5)  return 'agora mesmo';
  if (diff < 60) return 'há ' + diff + ' seg';
  var mins = Math.floor(diff / 60);
  if (mins < 60) return 'há ' + mins + ' min';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24)  return 'há ' + hrs + 'h';
  var days = Math.floor(hrs / 24);
  return 'há ' + days + 'd';
};

/**
 * @param extras  title, action_label, action_url, image_url — todos opcionais.
 *                Sem eles o toast se comporta como sempre: cabeçalho genérico
 *                e nenhum botão.
 */
/**
 * Põe uma linha no formato que a tela usa, venha ela da API ou do tempo real.
 *
 * A identidade é o id do MySQL. O espelho do Supabase tem um UUID próprio e
 * guarda o id do MySQL em `legacy_id`; como toda escrita (ler, excluir) vai
 * para a API por esse inteiro, é ele que precisa ser a chave — usar o UUID
 * obrigava a procurar o `legacy_id` no estado antes de cada ação, e a ação
 * simplesmente não acontecia quando ele faltava.
 */
var formatarLinha = function(n) {
  return Object.assign({}, n, {
    id: n.legacy_id != null ? n.legacy_id : n.id,
    read: !!n.read_at,
    created_at: n.created_at ? new Date(n.created_at).toISOString() : null,
    updated_at: n.updated_at ? new Date(n.updated_at).toISOString() : null,
  });
};

var showBrandedToast = function(type, message, onClose, extras) {
  var e = extras || {};
  playNotificationSound();

  var validTypes = ['success', 'error', 'warning', 'info'];
  var t = validTypes.indexOf(type) !== -1 ? type : 'info';
  var cfg = NOTIF_TYPE_CONFIG[t];
  var now = new Date();

  notification.open({
    message: React.createElement(
      'div',
      { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' } },
      React.createElement(
        'span',
        { style: { fontWeight: 700, fontSize: '13px', color: '#1f1f1f', letterSpacing: '0.01em' } },
        e.title || 'Nova notificação'
      ),
      React.createElement(
        'span',
        { style: { fontSize: '11px', color: '#9ca3af', fontWeight: 400, whiteSpace: 'nowrap' } },
        timeAgo(now)
      )
    ),
    description: React.createElement(
      'div',
      null,
      /* A capa diz que existe vídeo. O toast NÃO reproduz: ele flutua sobre o
         trabalho e some — pedir que alguém assista aqui é pedir atenção que o
         formato não sustenta. Quem quiser ver clica e vai para a página, onde
         o player tem capítulos e o changelog em volta. */
      e.image_url
        ? React.createElement('img', {
            src: e.image_url,
            alt: '',
            style: {
              width: '100%', maxWidth: '260px', borderRadius: '8px',
              display: 'block', marginBottom: '10px', border: '1px solid #ececf2',
            },
          })
        : null,
      React.createElement('span', {
        style: { color: '#4b5563', fontSize: '13px', lineHeight: '1.5', display: 'block' },
        dangerouslySetInnerHTML: { __html: message },
      }),
      e.action_url
        ? React.createElement(
            'button',
            {
              onClick: function() {
                notification.destroy();
                // history não alcança este módulo; a navegação do app é por
                // rota do react-router, e um assign recarregaria tudo
                window.dispatchEvent(new CustomEvent('app:navegar', { detail: e.action_url }));
              },
              style: {
                marginTop: '12px', border: 'none', cursor: 'pointer',
                background: cfg.color, color: '#fff', fontFamily: 'inherit',
                fontSize: '12.5px', fontWeight: 600, padding: '7px 14px',
                borderRadius: '7px',
              },
            },
            e.action_label || 'Abrir'
          )
        : null
    ),
    icon: React.createElement(
      'div',
      {
        style: {
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: cfg.color + '1a',
          border: '1.5px solid ' + cfg.color + '40',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }
      },
      React.createElement(Icon, {
        type: cfg.iconType,
        theme: 'filled',
        style: { fontSize: '18px', color: cfg.color }
      })
    ),
    style: {
      borderRadius: '12px',
      borderLeft: '4px solid ' + cfg.color,
      boxShadow: '0 10px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
      padding: '14px 18px 14px 14px',
      background: '#ffffff',
    },
    duration: 0,
    placement: 'topRight',
    onClose: onClose || function() {},
  });
};

// ─────────────────────────────────────────────────────────────────────────────

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  var history = useHistory();

  /**
   * O botão do toast navega por aqui.
   *
   * showBrandedToast é função de módulo, fora do React — não alcança o
   * history. Um window.location.assign resolveria, mas recarregaria o app
   * inteiro para trocar de rota. O evento faz a ponte: o toast dispara, o
   * provider (que vive dentro do Router) empurra a rota.
   */
  React.useEffect(function() {
    function navegar(e) {
      if (e && e.detail) history.push(e.detail);
    }
    window.addEventListener('app:navegar', navegar);
    return function() { window.removeEventListener('app:navegar', navegar); };
  }, [history]);

  const [notifications, setNotifications] = useState([]);
  const [loadingRead, setLoadingRead] = useState({});
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const mountedRef = useRef(true);
  const user = useSelector(function(state) { return state.user; });

  // Component mount/unmount
  useEffect(function() {
    mountedRef.current = true;
    return function() {
      mountedRef.current = false;
    };
  }, []);

  // Supabase Realtime subscription — activated once user.id is available
  useEffect(function() {
    if (!user || !user.id) return;

    var channel = null;

    var setup = async function() {
      console.log('[Notifications] setup() started, user.id=', user.id);

      // 1. Fetch Supabase JWT from Laravel (cached in localStorage for 23 h)
      var supabaseToken = getCachedToken(user.id);
      console.log('[Notifications] cached token?', !!supabaseToken);
      if (!supabaseToken) {
        try {
          var tokenRes = await request('GET', '/supabase-token', null, false);
          supabaseToken = tokenRes && tokenRes.data && tokenRes.data.token;
          var tokenDebug = tokenRes && tokenRes.data && tokenRes.data.debug;
          console.log('[Notifications] fetched fresh token?', !!supabaseToken);
          if (tokenDebug) {
            console.log('[Notifications] Token debug info:', JSON.stringify(tokenDebug, null, 2));
          }
          if (supabaseToken) setCachedToken(user.id, supabaseToken);
        } catch (err) {
          console.error('[Notifications] Error fetching Supabase token:', err);
          return;
        }
      }
      if (!supabaseToken) {
        console.error('[Notifications] No token — aborting setup');
        return;
      }

      if (!mountedRef.current) return;

      /* 2. A lista vem do Laravel, que é onde ela também é escrita.
         Já veio direto do Supabase, e isso deixava a tela lendo de um store e
         escrevendo em outro: marcar como lida e excluir sempre passaram pela
         API. Cada falha de espelhamento virava divergência visível — aviso que
         some da lista mas fica no banco, ou aviso que aparece e não pode ser
         dispensado porque não existe linha correspondente do outro lado.
         O Supabase segue no circuito só para o empurrão em tempo real. */
      try {
        var res = await request('GET', '/notifications');
        var rows = (res && res.data) || [];
        if (!mountedRef.current) return;
        console.log('[Notifications] Initial rows loaded:', rows.length);
        setNotifications(rows.map(formatarLinha));
      } catch (err) {
        console.error('[Notifications] Error fetching notifications:', err);
      }

      if (!mountedRef.current) return;

      // 3. Subscribe to Postgres changes.
      // Token injected at connection level (setAuth) AND channel level (params).
      // No column filter — RLS select_own policy handles row-level restriction.
      console.log('[Notifications] Setting connection-level auth token');
      console.log('[Notifications] Anon key length:', (process.env.REACT_APP_SUPABASE_ANON_KEY || '').length);
      console.log('[Notifications] Anon key last 10:', (process.env.REACT_APP_SUPABASE_ANON_KEY || '').slice(-10));
      supabase.realtime.setAuth(supabaseToken);

      // Debug: monitor raw WebSocket events
      var origConn = supabase.realtime.conn;
      if (origConn) {
        console.log('[Notifications] WebSocket readyState:', origConn.readyState);
      }
      // Hook into connection events after channel subscribe
      var checkWs = setInterval(function() {
        var ws = supabase.realtime.conn;
        if (ws && !ws._debugHooked) {
          ws._debugHooked = true;
          clearInterval(checkWs);
          console.log('[Notifications] WebSocket URL:', ws.url);
          ws.addEventListener('error', function(evt) {
            console.error('[Notifications] WS raw error event:', evt);
          });
          ws.addEventListener('close', function(evt) {
            console.error('[Notifications] WS close — code:', evt.code, 'reason:', evt.reason, 'wasClean:', evt.wasClean);
          });
        }
      }, 100);
      setTimeout(function() { clearInterval(checkWs); }, 10000);

      channel = supabase.channel('notifications:' + user.id);
      channel.params['user_token'] = supabaseToken;
      console.log('[Notifications] Channel created, params:', JSON.stringify(channel.params));

      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications'
          },
          function(payload) {
            console.log('[Notifications] Realtime event received:', payload.eventType, payload);
            if (!mountedRef.current) return;
            handleRealtimeEvent(payload);
          }
        )
        .subscribe(function(status, err) {
          console.log('[Notifications] Subscription status:', status, err || '');
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.error('[Notifications] Realtime channel failed:', status, err);
            console.error('[Notifications] Realtime URL:', process.env.REACT_APP_SUPABASE_URL);
            console.error('[Notifications] Token used (first 50 chars):', supabaseToken && supabaseToken.substring(0, 50));
            // Decode token to verify claims
            try {
              var tokenParts = supabaseToken.split('.');
              if (tokenParts.length === 3) {
                var claims = JSON.parse(atob(tokenParts[1]));
                console.error('[Notifications] Realtime token claims:', JSON.stringify(claims, null, 2));
              }
            } catch (e) {}
          }
          if (!mountedRef.current) return;
          // NOTE: Do NOT call setAuth here. Calling it on an already-joined channel
          // triggers an access_token push that causes a CHANNEL_ERROR → reconnect loop.
          // The token is already in channel.params['user_token'] and will be included
          // in every join message automatically, including after reconnects.
          setRealtimeConnected(status === 'SUBSCRIBED');
        });
    };

    setup();

    return function() {
      if (channel) {
        supabase.removeChannel(channel);
      }
      setRealtimeConnected(false);
    };
  }, [user && user.id]);

  var handleRealtimeEvent = function(payload) {
    var eventType = payload.eventType;
    var newRow = payload.new;
    var oldRow = payload.old;

    if (eventType === 'INSERT') {
      // Skip soft-deleted rows
      if (newRow.deleted_at) return;

      var formatted = formatarLinha(newRow);

      setNotifications(function(prev) {
        // O empurrão pode chegar depois de a lista já ter sido carregada com
        // esta linha; sem a checagem, o aviso apareceria em duplicata.
        if (prev.some(function(n) { return n.id === formatted.id; })) return prev;
        return [formatted].concat(prev);
      });

      showBrandedToast(newRow.type || 'info', newRow.message, function() {
        // Dispensar o toast marca como lida.
        if (formatted.id) {
          request('PATCH', '/notifications/' + formatted.id + '/read').catch(function() {});
          setNotifications(function(prev) {
            return prev.map(function(n) {
              return n.id === formatted.id ? Object.assign({}, n, { read: true }) : n;
            });
          });
        }
      }, {
        title: newRow.title,
        action_label: newRow.action_label,
        action_url: newRow.action_url,
        image_url: newRow.image_url,
      });
    } else if (eventType === 'UPDATE') {
      var atualizada = formatarLinha(newRow);
      setNotifications(function(prev) {
        return prev.map(function(n) {
          if (n.id !== atualizada.id) return n;
          if (newRow.deleted_at) return null;
          return Object.assign({}, n, atualizada);
        }).filter(Boolean);
      });
    } else if (eventType === 'DELETE') {
      var removidaId = oldRow.legacy_id != null ? oldRow.legacy_id : oldRow.id;
      setNotifications(function(prev) {
        return prev.filter(function(n) { return n.id !== removidaId; });
      });
    }
  };

  var handleMarkAsRead = async function(id) {
    if (loadingRead[id]) return;

    try {
      setLoadingRead(function(prev) { return Object.assign({}, prev, { [id]: true }); });
      await request('PATCH', '/notifications/' + id + '/read');
      setNotifications(function(prev) {
        return prev.map(function(n) {
          return n.id === id ? Object.assign({}, n, { read: true }) : n;
        });
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setLoadingRead(function(prev) { return Object.assign({}, prev, { [id]: false }); });
    }
  };

  var handleDelete = async function(id) {
    try {
      await request('DELETE', '/notifications/' + id);
      setNotifications(function(prev) {
        return prev.filter(function(n) { return n.id !== id; });
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  var handleMarkAllAsRead = async function() {
    try {
      await request('PATCH', '/notifications/read-all');
      setNotifications(function(prev) {
        return prev.map(function(n) { return Object.assign({}, n, { read: true }); });
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  var showToast = function(opts) {
    var type = opts.type || 'info';
    var message = opts.message;
    var description = opts.description;
    var duration = opts.duration !== undefined ? opts.duration : 4.5;
    notification[type]({
      message: message,
      description: description,
      duration: duration,
      placement: 'topRight'
    });
  };

  var showSuccess = function(msg, desc) { showToast({ type: 'success', message: msg, description: desc }); };
  var showError   = function(msg, desc) { showToast({ type: 'error',   message: msg, description: desc }); };
  var showWarning = function(msg, desc) { showToast({ type: 'warning', message: msg, description: desc }); };
  var showInfo    = function(msg, desc) { showToast({ type: 'info',    message: msg, description: desc }); };

  return (
    <NotificationContext.Provider
      value={{
        notifications: notifications,
        realtimeConnected: realtimeConnected,
        markAsRead: handleMarkAsRead,
        markAllAsRead: handleMarkAllAsRead,
        deleteNotification: handleDelete,
        showToast: showToast,
        showSuccess: showSuccess,
        showError: showError,
        showWarning: showWarning,
        showInfo: showInfo
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export var useNotification = function() {
  var context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
